/* eslint-disable no-await-in-loop, react-hooks/exhaustive-deps */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    Box,
    Typography,
    Button,
    ButtonGroup,
    Chip,
    Alert,
    alpha,
    CircularProgress,
    IconButton,
    Tooltip,
    TextField,
    InputAdornment,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Popover,
    Menu,
    MenuItem,
    List,
    ListItemButton,
    ListItemText,
    Link,
} from '@mui/material';
import { Transition } from 'src/components/dialogPopUp';
import ReportsAudienceDialog from 'src/components/dialogPopUp/ReportsAudienceDialog';
import Iconify from 'src/components/Iconify';
import { setAudienceIncludeArchive, updateSelectedAudience, setAudienceData } from 'src/redux/slices/audienceManager';
import { dispatch, useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { fetchArchivedLeadsInPeriod, fetchArchivedLeadsByCreated, parseApiDate, VALID_ARCHIVE_REASONS } from 'src/redux/slices/analyticsReports';
import { fetchUserEventDrilldown } from 'src/redux/slices/userPerformance';
import { normalizeLandingUrl } from 'src/utils/normalizeLandingUrl';
import { getReportLeadContactCount } from 'src/utils/reportLead';
import { STATUS_COLORS, STATUS_CONFIG, DATE_LABELS, buildPopupHeading } from 'src/constants/reportStatusPalette';
import { useBoolean, useTabs } from 'src/hooks';
import useReportExportFilename from 'src/hooks/useReportExportFilename';
import { DATE_TITLES } from 'src/constants/newLead';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import { fetchAppointmentStageRows, isAppointmentStage } from 'src/utils/appointmentStages';
import { selectMetricTreatment } from '../reportMetricSpecs';
import { mapEventToRow } from './eventRowMapper';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

// Chip palette for the Status column in the popup table. Aligned with the
// canonical palette used in the lead detail OverView (src/styles/OverView.js)
// so a lead's status chip looks the same everywhere in the app — with one
// Palette, header config, and date labels moved to a shared constants
// module so TreatmentLeadPopup and SourceLeadPopup can't drift.

// Source/Referral drilldown filter (case-insensitive). The Source table buckets a
// null/blank lead_source.type under the synthetic label 'Unknown', and the Referral
// table uses 'UNAVAILABLE' as the no-referral sentinel. When the click came from one
// of those buckets, also match leads whose value is blank/null — otherwise a literal
// equality (`type === 'unknown'`) never matches a null-source lead and the popup shows
// 0 while the cell shows N (staging QA: Source "Unknown" Consult Booked 12 vs popup 0).
// Real sources/referrals are unaffected: when `filter` isn't an empty-bucket label,
// this is exact equality as before.
export const EMPTY_GROUP_LABELS = new Set(['unknown', 'unavailable']);
export const matchesGroupFilter = (value, filter) => {
    if (!filter) return true;
    const v = String(value || '').toLowerCase();
    const f = String(filter).toLowerCase();
    if (EMPTY_GROUP_LABELS.has(f)) return v === '' || v === f;
    return v === f;
};

// Revenue drill-down value: the sum of custom_price over the lead's IN-SCOPE treatments
// (every treatment when idSet is null = "All") — exactly what the Projected Revenue KPI
// sums (Sum lead_treatments__custom_price). Null prices count as 0 and there is NO
// fallback to the catalog price, otherwise the popup total drifts from the KPI.
export const sumInScopePrices = (leadTreatments, idSet) => {
    const inScope = (leadTreatments || []).filter((lt) => !idSet || idSet.has(String(lt.treatment_data?.id ?? '')));
    return inScope.reduce((sum, lt) => sum + (Number(lt.custom_price) || 0), 0);
};

// Picks the lead treatment a By Treatment popup row is built from.
//  - NEW (created_at cohort) and any non-trusted caller: any in-scope treatment, preferring
//    one with the transition. With trustServerFilter on NEW the server filter is
//    authoritative, so fall back to lts[0] when the payload exposes no matchable id.
//  - Every other stage under trustServerFilter: /leads/ state_transition_params is LEAD-WIDE
//    (not pinned to the in-scope treatment), so require an IN-SCOPE treatment that itself
//    has the transition in range — otherwise the popup over-counts versus the funnel
//    (in-scope A never booked, out-of-scope B booked → must be excluded).
// Statuses that are NOT real pipeline states. 'OTHERS' is a matrix bucket of mixed
// statuses; the backend rejects it in state_transition_params (400). ARCHIVED is only
// valid via the dedicated archived path, never as a state_transition_params state.
export const NON_PIPELINE_STATUSES = ['OTHERS', 'ARCHIVED'];
export const isTransitionState = (status) => !!status && !NON_PIPELINE_STATUSES.includes(status);

export const selectScopedTreatment = (lts, { inScope, status, trustServerFilter, isEnquiriesPopup, hasTransitionInRange }) => {
    const scoped = (lts || []).filter(inScope);
    if (trustServerFilter && !isEnquiriesPopup) {
        return scoped.find((lt) => hasTransitionInRange(lt, status));
    }
    let match = scoped.find((lt) => hasTransitionInRange(lt, status)) || scoped[0];
    if (!match && trustServerFilter) match = (lts || [])[0] || {};
    return match;
};

export default function TreatmentLeadPopup({
    open,
    onClose,
    practiceId,
    // Full practice scope (analyticsPracticeIds). When >1 the lead requests add
    // practice__in so the popup counts the same practices as the cards/funnel.
    practiceIds,
    treatmentId,
    treatmentItemId,
    treatmentName,
    categoryName,
    // By Treatment report: the server-side filters (created_at / state_transition_params +
    // lead_treatments__treatment__in) are the source of truth the funnel counted with, so
    // rows are NOT trimmed client-side by per-treatment transition/created checks.
    trustServerFilter,
    // ARCHIVED drill-down by CREATED date + in-category treatment (funnel semantics),
    // instead of the Overview's archived_at semantics.
    archivedByCreated,
    // "Projected Revenue" drill-down: leads created in range with an in-category
    // treatment, showing treatment price, sorted by value (desc).
    revenueMode,
    // Additional drill-down filters — any lead not matching these is excluded
    // client-side after the base fetch. Used by Overview for source/referral/
    // practitioner/archived-reason clicks so every drilldown lands in the
    // same popup as KPI + funnel drilldowns. Named ...Filter to avoid
    // shadowing local lead.lead_source.type / lead_source.referral variables.
    sourceTypeFilter,
    referralFilter,
    landingUrlFilter,
    assignedToFilter,
    archivedReasonFilter,
    status,
    viewMode,
    dateRange,
    eventSource,
}) {
    const buildExportName = useReportExportFilename();
    const [searchQuery, setSearchQuery] = useState('');
    const [leadDetails, setLeadDetails] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState(null);
    // Event-sourced drilldown (By Team) — API total, distinct from
    // leadDetails.length which only reflects the loaded page.
    const [eventTotal, setEventTotal] = useState(null); // null = unknown/loading
    const fetchKeyRef = useRef(0);
    // Live progress so the user sees the popup is actually doing something
    // during the 20-40s pagination (backend caps limit=50, so a 300-lead query
    // is 6+ sequential round-trips).
    const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
    const [dateFormat, setDateFormat] = useState(() => localStorage.getItem('reportsPopupDateFormat') || 'human_readable');
    const [dateFormatAnchor, setDateFormatAnchor] = useState(null);
    // Sort state — null direction means "server order" (default). Click a header
    // to cycle: null → asc → desc → null.
    const [sortModel, setSortModel] = useState({ field: null, direction: null });
    // "Take Action" split-button menu anchor
    const [actionMenuAnchor, setActionMenuAnchor] = useState(null);

    // Audience dialog state
    const generateAudience = useBoolean();
    const { currentTab, onChangeTab } = useTabs('By Conditions');
    const [confirmation, setConfirmation] = useState(false);
    const [audienceName, setAudienceName] = useState('');
    const { audienceData } = useSelector((state) => state.audienceManager || {});

    const scopeIds = (practiceIds && practiceIds.length ? practiceIds : [practiceId]).filter(Boolean);
    const practiceInParam = scopeIds.length > 1 ? `&practice__in=${scopeIds.join(',')}` : '';

    const isCohortMode = viewMode === 'cohort';
    const isEnquiriesPopup = status === 'NEW';
    const dateColumnLabel = DATE_LABELS[status] || 'Date';
    let statusConfig = isEnquiriesPopup
        ? { label: 'All Enquiries', color: '#3b82f6', icon: 'eva:people-fill' }
        : STATUS_CONFIG[status] || STATUS_CONFIG.All;
    if (revenueMode) {
        statusConfig = { label: 'Projected Revenue', color: '#3b82f6', icon: 'eva:credit-card-fill' };
    }

    // Get the most recent transition timestamp for a given state from
    // state_transition_logs. Multi-cycle treatments (e.g. attended, went cold,
    // re-attended months later) can have several logs for the same state, and the
    // backend does NOT guarantee chronological order — so we sort by parsed
    // timestamp and take the latest rather than trusting array order. (Mirrors the
    // fix in analyticsReports.js getTransitionTimestamp.)
    const getTransitionTimestamp = (lt, targetState) => {
        const logs = lt.state_transition_logs || [];
        const matches = logs.filter((log) => log.to_state === targetState || log.state === targetState);
        if (matches.length === 0) return '';
        if (matches.length === 1) return matches[0].timestamp || '';
        const latest = [...matches].sort((a, b) => {
            const da = parseApiDate(a.timestamp);
            const db = parseApiDate(b.timestamp);
            return (db && db.isValid() ? db.valueOf() : 0) - (da && da.isValid() ? da.valueOf() : 0);
        })[0];
        return latest?.timestamp || '';
    };

    // Parse date string into dayjs object — delegates to the single
    // source of truth in analyticsReports.js. Handles DD-MM-YYYY with
    // microsecond timestamps from the backend correctly.
    const parseDate = (dateStr) => parseApiDate(dateStr);

    // Format date — supports date_format (DD/MM/YYYY) and human_readable (time ago)
    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        if (dateFormat === 'human_readable') {
            const d = parseDate(dateStr);
            return d ? d.fromNow() : '-';
        }
        const d = parseDate(dateStr);
        if (!d) return '-';
        if (dateFormat === 'date_time_format') return d.format('DD MMM YYYY, h:mm A');
        return d.format('DD MMM YYYY');
    };

    // Event time is deliberately NOT routed through formatDate above.
    //
    // Event mode renders one row per occurrence, so the time is load-bearing:
    // two appointments booked by the same user on the same day are
    // distinguishable ONLY by it. formatDate defaults to 'human_readable'
    // (d.fromNow()), which would render both of those rows as "2 days ago" —
    // collapsing the very distinction the event drilldown exists to show. The
    // 'DD MMM YYYY' branch drops the clock time and has the same effect.
    //
    // So the event column always renders an absolute UK-format date AND time,
    // independent of the user's format toggle. The toggle continues to govern
    // every lead-mode column exactly as before.
    const formatEventTime = (dateStr) => {
        if (!dateStr || dateStr === '—') return '—';
        const d = parseDate(dateStr);
        return d ? d.format('DD/MM/YYYY HH:mm') : '—';
    };

    // Handle date format toggle
    const handleDateFormatChange = (value) => {
        setDateFormat(value);
        localStorage.setItem('reportsPopupDateFormat', value);
        setDateFormatAnchor(null);
    };

    // Fetch leads. Two code paths:
    //   - ARCHIVED status: share fetchArchivedLeadsInPeriod with the Overview KPI
    //     widget and byReason breakdown so all three surfaces show the same count.
    //   - Everything else: paginate /leads/ with state_transition_params (works
    //     for NEW / CONSULTATION_* / TREATMENT_STARTED / CLOSED / FTA).
    const fetchLeads = useCallback(async () => {
        if (eventSource) return;
        if (!practiceId || !open) return;
        setIsLoading(true);
        setLoadError(null);
        setLoadProgress({ loaded: 0, total: 0 });

        try {
            const isArchivedPopup = status === 'ARCHIVED';
            // 'OTHERS' (mixed statuses) has no queryable state — never hit the API with it.
            if (status === 'OTHERS') {
                setLeadDetails([]);
                setIsLoading(false);
                return;
            }
            // `treatmentId` may be a single id OR a comma-joined list (category
            // views pass every PracticeTreatment id in the category, matching the
            // funnel aggregate). Parse to a Set for membership checks below.
            const treatmentIdSet = treatmentId
                ? new Set(String(treatmentId).split(',').map((s) => s.trim()).filter(Boolean))
                : null;

            // Source + Referral: keep them separate so the cell can render
            // referral as the primary label with source type as a subtitle.
            const sourceBits = (lead) => {
                const sourceType = lead.lead_source?.type || '';
                const referralName = lead.lead_source?.referral || '';
                return {
                    sourceType,
                    referralName,
                    sourcePrimary: referralName || sourceType || '-',
                    // Only show the secondary line when both pieces exist AND differ
                    sourceSecondary: referralName && sourceType && referralName !== sourceType ? sourceType : '',
                };
            };
            // Drill-down filters (Overview source/referral/practitioner/archive-reason
            // clicks), shared by the appointment-dated and transition-dated paths.
            // Case-insensitive comparison so "EMAIL" and "email" both match.
            const passesDrilldownFilters = (lead, matchLt, { sourceType, referralName }) => {
                if (!matchesGroupFilter(sourceType, sourceTypeFilter)) return false;
                if (!matchesGroupFilter(referralName, referralFilter)) return false;
                if (landingUrlFilter) {
                    const fromArray = (Array.isArray(lead.lead_metadata) ? lead.lead_metadata : [])
                        .find((m) => m?.key === 'entire_url')?.value;
                    const raw = String(fromArray || lead.lead_source?.metadata?.entire_url || '').trim();
                    const url = raw ? normalizeLandingUrl(raw) : '';
                    const wantEmpty = String(landingUrlFilter) === '(no landing URL)';
                    if (wantEmpty ? url !== '' : url.toLowerCase() !== String(landingUrlFilter).toLowerCase()) return false;
                }
                if (assignedToFilter) {
                    const assignees = [
                        matchLt.assigned_to,
                        ...(matchLt.assignees || []),
                        ...(lead.assigned_to ? [lead.assigned_to] : []),
                    ];
                    const assigneeIds = assignees
                        .filter(Boolean)
                        .map((a) => (typeof a === 'object' ? a.id : a))
                        .map(String);
                    if (!assigneeIds.includes(String(assignedToFilter))) return false;
                }
                if (archivedReasonFilter) {
                    // Match against the allowlist (mirror of backend ARCHIVE_REASON_NOTES)
                    // so a FOLLOW_UP note never wins over the real archive reason.
                    const validReasons = new Set(VALID_ARCHIVE_REASONS);
                    const noteReason = (lead.lead_notes || [])
                        .map((n) => n.note)
                        .find((note) => note && validReasons.has(note))
                        || lead.reason_for_archiving || lead.archived_reason || '';
                    if (String(noteReason) !== String(archivedReasonFilter)) return false;
                }
                return true;
            };

            // ── Archived path ────────────────────────────────────────────────
            // Uses the shared helper so widget / popup / breakdown stay coherent.
            // The helper paginates /archived/ and filters client-side by archived_at.
            if (isArchivedPopup) {
                if (!dateRange?.startDate || !dateRange?.endDate) {
                    setLeadDetails([]);
                    setIsLoading(false);
                    return;
                }
                const archivedLeads = archivedByCreated
                    ? await fetchArchivedLeadsByCreated(scopeIds, {
                        start: `${dateRange.startDate} 00:00:00`,
                        end: `${dateRange.endDate} 23:59:59`,
                        treatmentIds: treatmentIdSet ? Array.from(treatmentIdSet) : null,
                    }, { onProgress: (p) => setLoadProgress({ loaded: p.loaded, total: p.total }) })
                    : await fetchArchivedLeadsInPeriod(practiceId, {
                    start: `${dateRange.startDate} 00:00:00`,
                    end: `${dateRange.endDate} 23:59:59`,
                }, {
                    // The helper now sends archived_at__gte/lte on the URL
                    // directly, so if the backend honours the filter we short-
                    // circuit after the first page. We keep a higher cap as a
                    // safety net for practices with >1000 archived leads where
                    // the backend silently ignores the filter and we have to
                    // scan client-side (Reading Smiles was hitting this).
                    maxPages: 100,
                    onProgress: (p) => setLoadProgress({ loaded: p.loaded, total: p.total }),
                });

                // Apply drill-down filters to the archived path too. Before
                // phase-5 cleanup these were only applied to the non-
                // archived path at line ~418, so clicking "Archive Reasons"
                // with an archivedReasonFilter / sourceTypeFilter selected
                // showed ALL archived leads — not just matches — a silent
                // mismatch between the breakdown label and the drilldown
                // table. Now filters apply uniformly in both paths.
                const filteredArchivedLeads = archivedLeads.filter((lead) => {
                    const sourceType = lead.lead_source?.type || '';
                    const referralName = lead.lead_source?.referral || '';
                    if (!matchesGroupFilter(sourceType, sourceTypeFilter)) return false;
                    if (!matchesGroupFilter(referralName, referralFilter)) return false;
                    if (landingUrlFilter) {
                        // entire_url lives in either lead_metadata[].value (key='entire_url')
                        // or lead_source.metadata.entire_url — same resolution as
                        // fetchLandingUrlData. Normalise both sides so cell-click
                        // and popup share one URL grouping.
                        const fromArray = (Array.isArray(lead.lead_metadata) ? lead.lead_metadata : [])
                            .find((m) => m?.key === 'entire_url')?.value;
                        const raw = String(fromArray || lead.lead_source?.metadata?.entire_url || '').trim();
                        const url = raw ? normalizeLandingUrl(raw) : '';
                        const wantEmpty = String(landingUrlFilter) === '(no landing URL)';
                        if (wantEmpty ? url !== '' : url.toLowerCase() !== String(landingUrlFilter).toLowerCase()) return false;
                    }
                    if (assignedToFilter) {
                        const lts = lead.lead_treatments || [];
                        const assignees = lts.flatMap((lt) => [
                            lt.assigned_to,
                            ...(lt.assignees || []),
                        ]).concat(lead.assigned_to ? [lead.assigned_to] : []);
                        const assigneeIds = assignees
                            .filter(Boolean)
                            .map((a) => (typeof a === 'object' ? a.id : a))
                            .map(String);
                        if (!assigneeIds.includes(String(assignedToFilter))) return false;
                    }
                    if (archivedReasonFilter) {
                        const validReasons = new Set(VALID_ARCHIVE_REASONS);
                        const noteReason = (lead.lead_notes || [])
                            .map((n) => n.note)
                            .find((note) => note && validReasons.has(note))
                            || lead.reason_for_archiving || lead.archived_reason || '';
                        if (String(noteReason) !== String(archivedReasonFilter)) return false;
                    }
                    return true;
                });

                const details = filteredArchivedLeads.map((lead) => {
                    const lts = lead.lead_treatments || [];
                    const inScopeLt = treatmentIdSet
                        ? lts.find((lt) => treatmentIdSet.has(String(lt.treatment_data?.id ?? lt.treatment_id ?? '')))
                        : null;
                    const matchLt = inScopeLt || lts[0] || {};
                    const sourceType = lead.lead_source?.type || '';
                    const referralName = lead.lead_source?.referral || '';
                    const sourcePrimary = referralName || sourceType || '-';
                    const sourceSecondary = referralName && sourceType && referralName !== sourceType ? sourceType : '';
                    const timesContacted = getReportLeadContactCount(lead);
                    return {
                        leadId: lead.id,
                        leadName: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
                        treatment: (typeof matchLt.treatment === 'string' ? matchLt.treatment : null)
                            || matchLt.treatment_data?.treatment_category_name
                            || 'Other',
                        status: 'ARCHIVED',
                        price: parseFloat(matchLt.custom_price || matchLt.price || 0),
                        createdAt: archivedByCreated
                            ? (lead.created_at || '')
                            : (lead.archived_at || lead.created_at || ''),
                        source: sourcePrimary,
                        sourceSecondary,
                        timesContacted,
                    };
                });
                setLeadDetails(details);
                setIsLoading(false);
                return;
            }

            // ── Appointment-dated path (Consultation Attended / Failed to Attend) ──
            // Spec 2026-10-21 §3.1: these two stages are counted from the consultation
            // APPOINTMENT (final status, dated by its datetime). The population here
            // is exactly the representative appointments the card counted
            // (reports/appointment-stages/rows/), hydrated with the lead objects so
            // the columns (price, source, contacts) match every other status. The
            // cohort view keeps the transition basis until its backend moves.
            if (isAppointmentStage(status) && !isCohortMode) {
                if (!dateRange?.startDate || !dateRange?.endDate) {
                    setLeadDetails([]);
                    setIsLoading(false);
                    return;
                }
                const stageRows = await fetchAppointmentStageRows(scopeIds, {
                    stage: status,
                    start: dateRange.startDate,
                    end: dateRange.endDate,
                    treatmentIds: treatmentIdSet ? Array.from(treatmentIdSet) : null,
                    onProgress: (p) => setLoadProgress({ loaded: p.loaded, total: p.total }),
                });
                if (!stageRows.ok) {
                    throw new Error(stageRows.data?.detail || `Could not load the ${status} visits (HTTP ${stageRows.status || 'error'})`);
                }
                const visits = stageRows.rows;
                const leadIds = [...new Set(visits.map((v) => v.lead_id))];
                const leadById = new Map();
                const HYDRATE_PAGE = 50; // full lead objects: keep pages small (see the 13s/limit=500 note below)
                const HYDRATE_CONCURRENCY = 6;
                const idChunks = [];
                for (let i = 0; i < leadIds.length; i += HYDRATE_PAGE) idChunks.push(leadIds.slice(i, i + HYDRATE_PAGE));
                for (let i = 0; i < idChunks.length; i += HYDRATE_CONCURRENCY) {
                    const batch = idChunks.slice(i, i + HYDRATE_CONCURRENCY);
                    // eslint-disable-next-line no-await-in-loop
                    const pages = await Promise.all(batch.map((chunk) => callApi(
                        `/api/v1/practices/${practiceId}/leads/?id__in=${chunk.join(',')}&is_archived_included=true&limit=${HYDRATE_PAGE}${practiceInParam}`
                    )));
                    pages.forEach((page) => {
                        const r = page?.data;
                        (Array.isArray(r) ? r : (r?.results || [])).forEach((l) => leadById.set(l.id, l));
                    });
                }
                const details = visits.flatMap((visit) => {
                    const lead = leadById.get(visit.lead_id);
                    if (!lead) return [];
                    const lts = lead.lead_treatments || [];
                    const matchLt = lts.find((lt) => String(lt.id) === String(visit.lead_treatment_id)) || lts[0] || {};
                    const bits = sourceBits(lead);
                    if (!passesDrilldownFilters(lead, matchLt, bits)) return [];
                    return [{
                        leadId: lead.id,
                        leadName: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
                        treatment: (typeof matchLt.treatment === 'string' ? matchLt.treatment : null)
                            || visit.treatment_name
                            || matchLt.treatment_data?.treatment_category_name
                            || (lts.length ? 'Other' : 'No treatment'),
                        status: matchLt.status || status,
                        isArchived: Boolean(lead.is_archived),
                        archivedAt: lead.archived_at || null,
                        price: revenueMode
                            ? sumInScopePrices(lts, treatmentIdSet)
                            : parseFloat(matchLt.custom_price || matchLt.price || 0),
                        // The visit date (appointment datetime, ISO), not the transition time.
                        createdAt: visit.datetime,
                        undated: Boolean(visit.undated),
                        source: bits.sourcePrimary,
                        sourceSecondary: bits.sourceSecondary,
                        timesContacted: getReportLeadContactCount(lead),
                    }];
                });
                setLeadDetails(details);
                setIsLoading(false);
                return;
            }

            // ── Non-archived path (state_transition_params) ─────────────────
            // No fields restriction — fetch full lead objects so lead_source,
            // lead_contacted_counts, and state_transition_logs are all available.
            // Filter by PracticeTreatment ID — the /leads/ API does NOT support
            // lead_treatments__treatment__treatment_item (silently ignored, returns all leads).
            let treatmentParam = '';
            if (treatmentId) {
                treatmentParam = `&lead_treatments__treatment__in=${treatmentId}`;
            }

            let dateParam = '';
            if (dateRange?.startDate && dateRange?.endDate) {
                const startDate = `${dateRange.startDate} 00:00:00`;
                const endDate = `${dateRange.endDate} 23:59:59`;
                // For the initial enquiry stage (NEW), the lead's created_at is the
                // canonical date — there is no state_transition_log entry for the
                // initial state. Using state_transition_params for NEW returns 0 results
                // from the API, which contradicts the widget that counts by created_at.
                // For all other statuses, state_transition_params correctly filters by
                // when the lead entered that stage.
                if (isEnquiriesPopup) {
                    dateParam = `&created_at__gte=${startDate}&created_at__lte=${endDate}`;
                } else if (isTransitionState(status)) {
                    const stateParams = encodeURIComponent(JSON.stringify({ state: status, timestamp_gte: startDate, timestamp_lte: endDate }));
                    dateParam = `&state_transition_params=${stateParams}`;
                    if (isCohortMode) {
                        dateParam += `&created_at__gte=${startDate}&created_at__lte=${endDate}`;
                    }
                }
            }

            // Reporting contract: duplicates INCLUDED (reportMetricSpecs duplicates:'included').
            // No is_duplicate filter — it was a no-op (backend ignores it) and aggregate cell
            // counts include duplicates, so the popup must too (cell == popup).
            // Tie-break on id: created_at is not unique (bulk imports share timestamps), and
            // with a non-total order offset pages overlap/skip rows across the parallel page
            // requests — one lead appearing twice and another never (the "61 rows vs 62" bug).
            // By Treatment (trustServerFilter) mirrors the funnel, which excludes archived spam /
            // DUPLICATE_ENTRY noise — ask the server to do the same so rows == funnel number.
            const noiseParam = trustServerFilter ? '&exclude_report_noise=true' : '';
            const baseUrl = `/api/v1/practices/${practiceId}/leads/?is_archived_included=true${treatmentParam}${dateParam}${practiceInParam}${noiseParam}&ordering=-created_at,-id&limit=50`;

            // Fetch a SMALL first page (50) to learn the total count, then fan out
            // the rest in parallel batches. The page size must stay small: these
            // are full lead objects (no fields restriction), and the backend does
            // NOT cap the limit — asking for limit=500 returned all ~226 rows in a
            // single ~13s response that blew past callApi's 10s timeout, so the
            // request was aborted/retried forever and the popup stuck at "(0)".
            // 50-row pages return in a few seconds each and parallelise.
            const firstResponse = await callApi(baseUrl);
            const firstRaw = firstResponse?.data;
            const firstPage = Array.isArray(firstRaw) ? firstRaw : (firstRaw?.results || []);
            let allLeads = [...firstPage];
            setLoadProgress({ loaded: allLeads.length, total: firstRaw?.count || allLeads.length });

            // Derive the page size from what the server actually returned (may be < requested)
            const pageSize = firstPage.length || 50;
            const totalCount = firstRaw?.count ?? allLeads.length;
            const cap = Math.min(totalCount, 5000);

            if (allLeads.length < cap) {
                // Build the list of offsets we still need
                const remainingOffsets = [];
                for (let off = pageSize; off < cap; off += pageSize) remainingOffsets.push(off);

                // 6-wide concurrency to avoid hammering the API while still parallelising
                const CONCURRENCY = 6;
                const pathRoot = baseUrl.split('?')[0];
                const baseQuery = baseUrl.split('?')[1] || '';
                const queryWithoutOffset = baseQuery.replace(/&?offset=\d+/, '').replace(/&?limit=\d+/, '');
                for (let i = 0; i < remainingOffsets.length; i += CONCURRENCY) {
                    const batch = remainingOffsets.slice(i, i + CONCURRENCY);
                    // eslint-disable-next-line no-await-in-loop
                    const pages = await Promise.all(batch.map((off) =>
                        callApi(`${pathRoot}?limit=${pageSize}&offset=${off}&${queryWithoutOffset}`)
                    ));
                    const newRows = pages.flatMap((p) => {
                        const r = p?.data;
                        return Array.isArray(r) ? r : (r?.results || []);
                    });
                    allLeads = allLeads.concat(newRows);
                    setLoadProgress({ loaded: allLeads.length, total: totalCount });
                    if (allLeads.length >= cap) break;
                }
            }

            // Safety net: never let a lead render twice if pages still overlap.
            {
                const seenIds = new Set();
                allLeads = allLeads.filter((l) => (seenIds.has(l.id) ? false : (seenIds.add(l.id), true)));
            }

            // Client-side filter: the /leads/ endpoint's state_transition_params
            // is NOT treatment-scoped — it returns leads where ANY treatment had
            // the target transition. We must verify that the MATCHING treatment
            // actually has the right transition in its logs.
            const rangeStart = dateRange?.startDate ? dayjs(`${dateRange.startDate} 00:00:00`) : null;
            const rangeEnd = dateRange?.endDate ? dayjs(`${dateRange.endDate} 23:59:59`) : null;

            // Backend timestamps come as DD-MM-YYYY with MICROSECOND precision
            // + a Z suffix (e.g. "13-03-2026 14:48:51.213812Z"). Two issues to
            // handle: (1) flip DD-MM-YYYY to YYYY-MM-DD, (2) truncate fractional
            // seconds to milliseconds — dayjs's default parser silently returns
            // Invalid Date for 6-digit fractional seconds, which was causing
            // EVERY lead to be filtered out downstream.
            // Use the shared parseApiDate (UTC-correct, handles backend DD-MM-YYYY
            // with microseconds). Returns null on invalid; callers use ?. chaining.
            const parseTimestamp = parseApiDate;

            const hasTransitionInRange = (lt, targetState) => {
                const logs = lt.state_transition_logs || [];
                return logs.some((log) => {
                    const logState = log.to_state || log.state;
                    if (logState !== targetState) return false;
                    if (!rangeStart || !rangeEnd) return true;
                    const ts = parseTimestamp(log.timestamp);
                    return ts?.isValid() && !ts.isBefore(rangeStart) && !ts.isAfter(rangeEnd);
                });
            };

            const details = allLeads.flatMap((lead) => {
                const lts = lead.lead_treatments || [];

                // Find the treatment matching our filter.
                // Priority: TreatmentItem ID > PracticeTreatment ID > treatment name string >
                //           treatment_category name (when categoryName prop is set).
                // The API filters by TreatmentItem, but a practice can have multiple
                // PracticeTreatment records for the same item (disabled/re-enabled),
                // so matching by PracticeTreatment ID alone can miss leads.
                // When a specific treatment is targeted, find it. Otherwise find
                // the treatment that actually had the target transition (not just lts[0]).
                let matchLt;
                if (treatmentItemId || treatmentIdSet) {
                    // A lead can carry several treatments. With a multi-treatment
                    // (category) filter, prefer one that actually transitioned to the
                    // target status in range; otherwise fall back to any in-scope
                    // treatment. (For a single id this collapses to the old behaviour.)
                    const inScope = (lt) => {
                        const tiRaw = lt.treatment_data?.treatment_item;
                        const tiId = tiRaw && typeof tiRaw === 'object' ? tiRaw.id : tiRaw;
                        if (tiId && treatmentItemId && String(tiId) === String(treatmentItemId)) return true;
                        const tdId = lt.treatment_data?.id;
                        if (tdId && treatmentIdSet && treatmentIdSet.has(String(tdId))) return true;
                        if (typeof lt.treatment === 'string' && treatmentName) {
                            return lt.treatment.toLowerCase() === treatmentName.toLowerCase();
                        }
                        return false;
                    };
                    matchLt = selectScopedTreatment(lts, {
                        inScope, status, trustServerFilter, isEnquiriesPopup, hasTransitionInRange,
                    });
                } else if (categoryName) {
                    // Category filter (Overview "Treatment Breakdown" clicks) — match
                    // any treatment whose category.name equals the clicked category.
                    const target = categoryName.toLowerCase();
                    matchLt = lts.find((lt) => {
                        const cat = lt.treatment_data?.treatment_category_name
                            || lt.treatment_data?.treatment_item?.treatment_category?.name
                            || '';
                        return cat.toLowerCase() === target;
                    });
                } else {
                    // No explicit treatment/category scope — the metric spec's
                    // population UNIT decides (see reportMetricSpecs.js):
                    //   NEW    → lead-unit: one row per lead, treatment-less included.
                    //   status → transition-unit: requires a transitioned treatment;
                    //            a treatment-less lead is dropped.
                    const { emit, matchLt: selectedLt } = selectMetricTreatment(lead, status, { hasTransitionInRange });
                    if (!emit) return [];
                    matchLt = selectedLt;
                }
                // Treatment/category-scoped branches above leave matchLt undefined when
                // the lead has no in-scope treatment → drop it.
                if (!matchLt) return [];

                // Verify this treatment actually had the target transition in the date range.
                // state_transition_params on /leads/ is lead-wide, not treatment-scoped.
                if (trustServerFilter) {
                    // Server-side created_at / state_transition_params is authoritative
                    // (it is what the funnel counted) — no client-side trimming.
                } else if (isArchivedPopup) {
                    // Client-side filter: check archived_at falls within date range
                    if (rangeStart && rangeEnd) {
                        const archivedAt = parseTimestamp(lead.archived_at);
                        if (!archivedAt?.isValid() || archivedAt.isBefore(rangeStart) || archivedAt.isAfter(rangeEnd)) {
                            return [];
                        }
                    }
                } else if (isEnquiriesPopup) {
                    // NEW: verify lead was created in range. The parent widget
                    // ("Total Leads") uses `created_at__gte/lte` to count, so the
                    // popup must apply the same semantic — otherwise re-opened /
                    // re-transitioned leads leak in and the popup over-counts
                    // (widget=16, popup=18 was the reported drift).
                    if (rangeStart && rangeEnd) {
                        const created = parseTimestamp(lead.created_at);
                        if (!created?.isValid() || created.isBefore(rangeStart) || created.isAfter(rangeEnd)) {
                            return [];
                        }
                    }
                } else if (treatmentItemId || treatmentId) {
                    // When filtering by a specific treatment, verify client-side that
                    // THIS treatment had the transition (server filter is lead-wide).
                    if (!hasTransitionInRange(matchLt, status)) return [];
                }
                // When no treatment filter (funnel/KPI clicks), trust the server's
                // state_transition_params filter — it already verified the lead had
                // the transition. Client-side re-checking drops leads with unparseable timestamps.

                // For non-enquiry statuses, prefer the transition date over created_at
                let dateValue = lead.created_at || '';
                if (isArchivedPopup) {
                    dateValue = lead.archived_at || lead.created_at || '';
                } else if (!isEnquiriesPopup) {
                    const transitionDate = getTransitionTimestamp(matchLt, status);
                    if (transitionDate) dateValue = transitionDate;
                }

                const { sourcePrimary, sourceSecondary, ...sourceNames } = sourceBits(lead);
                if (!passesDrilldownFilters(lead, matchLt, sourceNames)) return [];

                const timesContacted = getReportLeadContactCount(lead);

                return [{
                    leadId: lead.id,
                    leadName: `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unknown',
                    treatment: (typeof matchLt.treatment === 'string' ? matchLt.treatment : null) || matchLt.treatment_data?.treatment_category_name || (lts.length ? 'Other' : 'No treatment'),
                    status: matchLt.status || 'NEW',
                    // Lead-level archive state. Deliberately NOT folded into `status`:
                    // `status` answers "how far did this lead get?" (this one reached
                    // CONSULTATION_ATTENDED before being archived) and overwriting it
                    // with ARCHIVED would make every archived lead look identical.
                    isArchived: Boolean(lead.is_archived),
                    archivedAt: lead.archived_at || null,
                    price: revenueMode
                        ? sumInScopePrices(lts, treatmentIdSet)
                        : parseFloat(matchLt.custom_price || matchLt.price || 0),
                    createdAt: dateValue,
                    source: sourcePrimary,
                    sourceSecondary,
                    timesContacted,
                }];
            });

            setLeadDetails(details);
        } catch (error) {
            console.error('Failed to fetch treatment lead details', error);
            setLeadDetails([]);
            setLoadError(error?.message || 'Failed to load leads');
        } finally {
            setIsLoading(false);
        }
        // Depend on the date STRING values, not the `dateRange` object identity.
        // Parents pass `dateRange={{ startDate, endDate }}` as a fresh object literal
        // every render, so keying on the object made this useCallback (and the
        // open/fetchLeads effect below) re-fire on every parent re-render, aborting
        // the in-flight /leads/ pagination before it finished — the drilldown popup
        // stuck at "(0)" while the KPI tile showed the real count. The string values
        // are stable across renders, so the fetch now runs once and completes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, open, status, treatmentId, treatmentItemId, categoryName, sourceTypeFilter, referralFilter, landingUrlFilter, assignedToFilter, archivedReasonFilter, dateRange?.startDate, dateRange?.endDate, viewMode, eventSource, practiceIds?.join(','), trustServerFilter, archivedByCreated, revenueMode]);

    // Event-sourced drilldown (By Team). Bypasses the /leads/ pagination and the
    // assignee filter entirely — the column counts UserEvent rows by actor, so the
    // popup must read the same source or it cannot reconcile. Extracted as a
    // callback (not inlined in the effect) so the error-state Retry control can
    // re-run it directly, the same way the lead path's Retry calls fetchLeads.
    const fetchEvents = useCallback(async () => {
        if (!open || !eventSource) return;
        fetchKeyRef.current += 1;
        const key = fetchKeyRef.current;
        const isStale = () => fetchKeyRef.current !== key;   // pattern from userPerformance.js:144

        setIsLoading(true);
        setLoadError(null);
        setEventTotal(null);
        setLeadDetails([]);

        const res = await fetchUserEventDrilldown({ practiceId, ...eventSource });
        if (isStale()) return;                            // a newer open superseded this one
        if (!res.ok) {
            setLoadError(res.error);
            setIsLoading(false);
            return;
        }
        setEventTotal(res.count);
        setLeadDetails(res.results.map(mapEventToRow));
        setIsLoading(false);
    }, [open, practiceId, eventSource?.practiceUserId, eventSource?.eventType,
        eventSource?.startDate, eventSource?.endDate]);

    // Declared (and fired) before the lead-fetch effect below so, in event mode,
    // this is the one that actually runs — fetchLeads is a no-op when eventSource
    // is set (see its guard above).
    useEffect(() => {
        fetchEvents();
    }, [fetchEvents]);

    useEffect(() => {
        if (open) {
            setSearchQuery('');
            // Revenue drill-down lists leads by value, highest first.
            setSortModel(revenueMode ? { field: 'price', direction: 'desc' } : { field: null, direction: null });
            fetchLeads();
        }
    }, [open, fetchLeads]);

    // Client-side search filter + sort. Sort applies after filter so the row
    // numbers and total match what the user sees.
    const filteredData = useMemo(() => {
        const query = searchQuery.toLowerCase();
        const filtered = searchQuery
            ? leadDetails.filter((row) =>
                row.leadName.toLowerCase().includes(query)
                || row.treatment.toLowerCase().includes(query))
            : leadDetails;

        if (!sortModel.field || !sortModel.direction) return filtered;

        const compareFns = {
            leadName: (a, b) => a.leadName.localeCompare(b.leadName),
            treatment: (a, b) => (a.treatment || '').localeCompare(b.treatment || ''),
            status: (a, b) => (a.status || '').localeCompare(b.status || ''),
            source: (a, b) => (a.source || '').localeCompare(b.source || ''),
            timesContacted: (a, b) => (a.timesContacted || 0) - (b.timesContacted || 0),
            createdAt: (a, b) => {
                const ta = a.createdAt ? new Date(a.createdAt.replace(/^(\d{2})-(\d{2})-(\d{4})/, '$3-$2-$1')).getTime() : 0;
                const tb = b.createdAt ? new Date(b.createdAt.replace(/^(\d{2})-(\d{2})-(\d{4})/, '$3-$2-$1')).getTime() : 0;
                return ta - tb;
            },
            price: (a, b) => (a.price || 0) - (b.price || 0),
        };
        const cmp = compareFns[sortModel.field];
        if (!cmp) return filtered;
        const sorted = [...filtered].sort(cmp);
        return sortModel.direction === 'desc' ? sorted.reverse() : sorted;
    }, [leadDetails, searchQuery, sortModel]);

    // Event mode only: fetchEvents requests a single page (limit=200, see its
    // call to fetchUserEventDrilldown above) and never paginates — deliberately,
    // to avoid the ~9 sequential round trips 1000+ events would need against
    // callApi's 10s per-request timeout (the same failure mode documented at
    // the /leads/ pagination above, which left the popup stuck at "(0)"). So
    // `leadDetails.length` (rows actually loaded) can be smaller than
    // `eventTotal` (the server's true count, already shown in the heading).
    //
    // Compared against leadDetails, NOT filteredData: filteredData is the
    // search-filtered/sorted view, and it must not be the denominator here —
    // a user typing into the search box would otherwise watch the "showing N
    // of M" figure change too, which reads as if the search result set were
    // truncated rather than the underlying loaded page. This notice is about
    // how much of the SERVER'S data made it into the popup at all, a fact
    // that doesn't change when the user searches, so it stays pinned to the
    // loaded-row count regardless of the search query.
    const isEventTruncated = Boolean(eventSource) && eventTotal !== null && eventTotal > leadDetails.length;

    // Cycle sort: null → asc → desc → null for the same field; new field resets to asc.
    const toggleSort = (field) => {
        setSortModel((prev) => {
            if (prev.field !== field) return { field, direction: 'asc' };
            if (prev.direction === 'asc') return { field, direction: 'desc' };
            return { field: null, direction: null };
        });
    };

    const sortIcon = (field) => {
        if (sortModel.field !== field) return 'eva:chevron-down-outline';
        return sortModel.direction === 'asc' ? 'eva:arrow-up-outline' : 'eva:arrow-down-outline';
    };

    // Export CSV (matching ClinicianConversionTracker format)
    const handleExportCSV = useCallback(() => {
        if (!filteredData || filteredData.length === 0) return;
        // Event mode: the table renders event columns (#, Lead Name, Treatment,
        // Source, Event type, Event time, Description) — none of `status`,
        // `timesContacted`, `price` or `createdAt` exist on an event row (see
        // eventRowMapper.js), so the export must mirror the EVENT columns, not
        // the lead-shaped ones below. Lead-mode export is untouched.
        const headers = eventSource
            ? ['#', 'Lead Name', 'Treatment', 'Source', 'Event type', 'Event time', 'Description']
            : ['#', 'Lead Name', 'Treatment', 'Status', 'Source', 'Times contacted', dateColumnLabel, 'Price'];
        const csvRows = filteredData.map((row, idx) => {
            if (eventSource) {
                return [
                    idx + 1,
                    row.leadName || '-',
                    row.treatment || '-',
                    row.source || '-',
                    row.eventType ? row.eventType.replace(/_/g, ' ') : '—',
                    formatEventTime(row.eventTime),
                    row.description || '-',
                ];
            }
            let dateStr = '-';
            if (row.createdAt) {
                try {
                    const parts = row.createdAt.match(/^(\d{2})-(\d{2})-(\d{4})/);
                    if (parts) {
                        dateStr = `${parts[1]}/${parts[2]}/${parts[3]}`;
                    } else {
                        const d = new Date(row.createdAt);
                        if (!Number.isNaN(d.getTime())) dateStr = d.toLocaleDateString('en-GB');
                    }
                } catch { /* ignore */ }
            }
            // The Source cell shows a primary line + optional secondary line;
            // fold both into the single Source column the table displays.
            const sourceStr = row.sourceSecondary
                ? `${row.source || '-'} (${row.sourceSecondary})`
                : (row.source || '-');
            return [
                idx + 1,
                row.leadName || '-',
                row.treatment || '-',
                STATUS_COLORS[row.status]?.label || row.status?.replace(/_/g, ' ') || '-',
                sourceStr,
                row.timesContacted || 0,
                dateStr,
                row.price > 0 ? `£${row.price.toLocaleString()}` : '-',
            ];
        });
        const csvContent = [headers, ...csvRows].map((r) => r.map((cell) => `"${cell}"`).join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        // Event mode: name the file after events, not leads — there is no
        // lead-status/cohort concept behind a list of events. When the popup
        // only ever loaded a partial page (see isEventTruncated above), that
        // must be visible in the filename itself: the CSV is the only thing
        // that travels once downloaded, and a week later the filename is the
        // sole record of whether it's the full dataset or the first slice.
        const exportSlug = eventSource
            ? `${(treatmentName || 'events').replace(/\s+/g, '-')}-events${isEventTruncated ? `-first-${leadDetails.length}-of-${eventTotal}` : ''}`
            : `${(treatmentName || 'treatment').replace(/\s+/g, '-')}-${status}-${isCohortMode ? 'cohort' : 'activity'}-leads`;
        link.download = buildExportName(exportSlug, dateRange);
        link.click();
        URL.revokeObjectURL(link.href);
    }, [filteredData, treatmentName, status, isCohortMode, dateColumnLabel, dateRange, buildExportName, eventSource, formatDate, formatEventTime, isEventTruncated, leadDetails.length, eventTotal]);

    // Audience handlers
    const handleCloseAudienceDialog = () => {
        generateAudience.onFalse();
        setAudienceName('');
    };

    const handleCreateAudience = () => {
        generateAudience.onTrue();
        dispatch(updateSelectedAudience({ ...audienceData }));
        dispatch(setAudienceIncludeArchive(false));
    };

    const handleCreateAudienceSuccess = (data) => {
        setConfirmation(true);
        generateAudience.onFalse();
        onClose();
        setAudienceName(data.name);
    };

    const handleClose = () => {
        setSearchQuery('');
        setLeadDetails([]);
        dispatch(setAudienceData({}));
        onClose();
    };

    return (
        <>
            <Dialog
                TransitionComponent={Transition}
                open={open}
                onClose={handleClose}
                maxWidth="xl"
                fullWidth
                data-testid="treatment-lead-popup"
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        height: 'auto',
                        maxHeight: '90vh',
                    },
                }}
            >
                {/* Header */}
                <Box sx={{ p: 0 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 2.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.08)} 0%, ${alpha('#3b82f6', 0.02)} 100%)`,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                sx={{
                                    width: 44,
                                    height: 44,
                                    borderRadius: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(statusConfig.color, 0.1),
                                }}
                            >
                                <Iconify
                                    icon={statusConfig.icon}
                                    width={22}
                                    height={22}
                                    sx={{ color: statusConfig.color }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '18px' }}>
                                    {/* Event mode: statusConfig falls back to STATUS_CONFIG.All when the
                                      * caller omits `status` (event drilldowns aren't opened with a lead
                                      * status), so appending statusConfig.label here would render
                                      * "<name> — <event label> — All Leads". Render treatmentName alone.
                                      * Total is the API `count`, never the loaded-rows length — and is
                                      * suppressed entirely while loading OR erroring, so neither a
                                      * slow/timed-out request nor a failed one can read as "0 events"
                                      * (see the loadError block below). */}
                                    {eventSource ? treatmentName : buildPopupHeading(treatmentName, statusConfig.label)}
                                    {(isLoading || loadError) ? '' : ` (${eventSource ? (eventTotal ?? 0) : filteredData.length}${eventSource ? ' events' : ''})`}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                                    {/* Event mode: there is no single lead status behind a list of
                                      * events (statusConfig falls back to STATUS_CONFIG.All, "All
                                      * Leads", since callers don't pass `status` for event drilldowns
                                      * — see the heading comment above). Rendering that chip here would
                                      * be actively misleading directly under a heading that correctly
                                      * reads "… (N events)", so it's suppressed entirely in event mode. */}
                                    {!eventSource && (
                                        <Chip
                                            label={statusConfig.label}
                                            size="small"
                                            sx={{
                                                bgcolor: alpha(statusConfig.color, 0.1),
                                                color: statusConfig.color,
                                                fontWeight: 600,
                                                fontSize: '11px',
                                                height: 22,
                                            }}
                                        />
                                    )}
                                    {isCohortMode && (
                                        <Chip
                                            label="Cohort"
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                bgcolor: alpha('#3b82f6', 0.1),
                                                color: '#3b82f6',
                                            }}
                                        />
                                    )}
                                    {dateRange && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '12px' }}>
                                            {dayjs(dateRange.startDate).format('DD MMM YYYY')} to {dayjs(dateRange.endDate).format('DD MMM YYYY')}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Tooltip title="Export CSV" arrow>
                                <IconButton
                                    size="small"
                                    onClick={handleExportCSV}
                                    disabled={!filteredData?.length}
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        border: '1px solid',
                                        borderColor: '#e2e8f0',
                                        borderRadius: '8px',
                                        '&:hover': { bgcolor: alpha('#3b82f6', 0.08) },
                                    }}
                                >
                                    <Iconify icon="eva:download-outline" width={18} sx={{ color: '#64748b' }} />
                                </IconButton>
                            </Tooltip>
                            {/* Take Action split button — primary click creates an audience (existing flow).
                              * The caret opens a menu with future operational actions (To-Do list, etc).
                              *
                              * Event mode: several event rows can point at the same lead (a lead booked
                              * twice is two ROWS but one LEAD). "Take Action" (Create Audience) resolves
                              * its target leads by re-running the popup's status/treatment/date filters
                              * (see ReportsAudienceDialog — it has no id__in mechanism to target the
                              * exact distinct-lead set from distinctLeadIdsForAction), which cannot
                              * reconstruct "the leads behind these events" and would silently
                              * mis-target. Disabling here is the safe choice per the task: a disabled
                              * control beats acting on the wrong/duplicated leads. */}
                            <Tooltip title={eventSource ? 'Not available for event drilldowns' : ''} arrow>
                                <span>
                                    <ButtonGroup variant="contained" size="small" disabled={!filteredData?.length || Boolean(eventSource)}>
                                        <Button
                                            onClick={handleCreateAudience}
                                            startIcon={<Iconify icon="mdi:target" width={16} />}
                                            sx={{ textTransform: 'none', borderRadius: '8px 0 0 8px', pr: 1.5 }}
                                        >
                                            Take Action
                                        </Button>
                                        <Button
                                            onClick={(e) => setActionMenuAnchor(e.currentTarget)}
                                            sx={{ textTransform: 'none', borderRadius: '0 8px 8px 0', px: 0.5, minWidth: 28, borderLeft: '1px solid rgba(255,255,255,0.25)' }}
                                            aria-label="More actions"
                                        >
                                            <Iconify icon="eva:chevron-down-fill" width={16} />
                                        </Button>
                                    </ButtonGroup>
                                </span>
                            </Tooltip>
                            <Menu
                                anchorEl={actionMenuAnchor}
                                open={Boolean(actionMenuAnchor)}
                                onClose={() => setActionMenuAnchor(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { mt: 0.5, minWidth: 280, borderRadius: '10px' } }}
                            >
                                <MenuItem
                                    onClick={() => { setActionMenuAnchor(null); handleCreateAudience(); }}
                                    sx={{ gap: 1.5, py: 1.25, alignItems: 'flex-start' }}
                                >
                                    <Iconify icon="mdi:email-multiple-outline" width={20} sx={{ color: '#3b82f6', mt: 0.25 }} />
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Create Audience</Typography>
                                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>Message via Email or WhatsApp</Typography>
                                    </Box>
                                </MenuItem>
                                <Tooltip title="Coming soon — assign these leads to team members as an action list" arrow placement="left">
                                    <span>
                                        <MenuItem disabled sx={{ gap: 1.5, py: 1.25, alignItems: 'flex-start', opacity: '1 !important' }}>
                                            <Iconify icon="mdi:clipboard-check-outline" width={20} sx={{ color: '#94a3b8', mt: 0.25 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#94a3b8' }}>Create To-Do List</Typography>
                                                    <Chip
                                                        label="Coming soon"
                                                        size="small"
                                                        sx={{ height: 18, fontSize: '10px', fontWeight: 600, bgcolor: '#ede9fe', color: '#7c3aed' }}
                                                    />
                                                </Box>
                                                <Typography sx={{ fontSize: '11px', color: '#cbd5e1' }}>Assign to team members to action</Typography>
                                            </Box>
                                        </MenuItem>
                                    </span>
                                </Tooltip>
                            </Menu>
                            <IconButton onClick={handleClose} size="small">
                                <Iconify icon="eva:close-fill" width={24} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Search Bar */}
                    <Box sx={{ px: 2.5, pt: 2 }}>
                        <TextField
                            size="small"
                            placeholder="Search by name or treatment..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                width: { xs: '100%', sm: 280 },
                                '& .MuiOutlinedInput-root': {
                                    bgcolor: '#fff',
                                    borderRadius: '8px',
                                },
                            }}
                        />
                    </Box>

                    {/* Truncation notice — event mode only, and only when the loaded
                      * page (leadDetails) is smaller than the server's real total
                      * (eventTotal). Placed directly under the search bar — above the
                      * table, not the bottom — so a user who exports without scrolling
                      * still sees it before they click. See isEventTruncated above for
                      * why this is keyed off leadDetails.length rather than
                      * filteredData.length. */}
                    {isEventTruncated && (
                        <Box sx={{ px: 2.5, pt: 1.5 }}>
                            <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                                {`Showing the first ${leadDetails.length.toLocaleString()} of ${eventTotal.toLocaleString()} events. Narrow the date range to see the rest.`}
                            </Alert>
                        </Box>
                    )}

                    {/* Table — matches ClinicianConversionTracker popup design.
                      *
                      * Header cells all carry `whiteSpace: nowrap` so dynamic labels
                      * like "Booked Date" / "Attended Date" / "FTA Date" never wrap.
                      * Body cells do the same; the Treatment column truncates with
                      * an ellipsis + full-value tooltip so one long treatment name
                      * can't force the whole row to grow. If the combined width
                      * ever exceeds the dialog, the scroll container handles it. */}
                    {/* eslint-disable-next-line no-nested-ternary */}
                    {isLoading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
                            <CircularProgress
                                variant={loadProgress.total > 0 ? 'determinate' : 'indeterminate'}
                                value={loadProgress.total > 0 ? Math.min(100, Math.round((loadProgress.loaded / loadProgress.total) * 100)) : undefined}
                            />
                            {loadProgress.total > 0 && (
                                <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                                    Loading {loadProgress.loaded.toLocaleString()} of {loadProgress.total.toLocaleString()} leads…
                                </Typography>
                            )}
                        </Box>
                    ) : loadError ? (
                        // Distinguishable from the empty state below ("No events" / "No data") —
                        // a failed request must never render like zero results (see the "(0)" heading
                        // incident referenced above).
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 6 }}>
                            <Iconify icon="eva:alert-triangle-outline" sx={{ fontSize: 40, color: '#dc2626' }} />
                            {eventSource ? (
                                <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#dc2626', maxWidth: 480, textAlign: 'center' }}>
                                    {`Events could not be loaded — ${loadError}`}
                                </Typography>
                            ) : (
                                <>
                                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#dc2626' }}>
                                        Couldn&rsquo;t load leads
                                    </Typography>
                                    <Typography sx={{ fontSize: '12px', color: '#64748b', maxWidth: 480, textAlign: 'center' }}>
                                        {loadError}
                                    </Typography>
                                </>
                            )}
                            <Button variant="outlined" size="small" onClick={eventSource ? fetchEvents : fetchLeads} sx={{ mt: 1 }}>
                                Retry
                            </Button>
                        </Box>
                    ) : (
                        <Box sx={{ minHeight: 520, maxHeight: 640, overflow: 'auto' }}>
                            <Table size="medium" stickyHeader sx={{ minWidth: 900 }}>
                                <TableHead>
                                    {eventSource ? (
                                        // Event mode: a lead's CURRENT status is unrelated to a historical
                                        // event, so the Status column is omitted entirely rather than
                                        // showing a value that could contradict the event being viewed.
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', width: 48 }}>#</TableCell>
                                            {['Lead Name', 'Treatment', 'Source', 'Event type', 'Event time', 'Description'].map((label) => (
                                                <TableCell key={label} sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap' }}>
                                                    {label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    ) : (
                                        <TableRow>
                                            {/* Row-number column isn't sortable. */}
                                            <TableCell sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', width: 48 }}>#</TableCell>
                                            {/* Sortable headers — click cycles null → asc → desc → null.
                                              * The sort icon stays present so users always know the column is sortable. */}
                                            {[
                                                { field: 'leadName', label: 'Lead Name' },
                                                { field: 'treatment', label: 'Treatment' },
                                                { field: 'status', label: 'Status' },
                                                { field: 'source', label: 'Source' },
                                            ].map(({ field, label }) => (
                                                <TableCell
                                                    key={field}
                                                    sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } }}
                                                    onClick={() => toggleSort(field)}
                                                >
                                                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                                        {label}
                                                        <Iconify
                                                            icon={sortIcon(field)}
                                                            width={13}
                                                            height={13}
                                                            sx={{ color: sortModel.field === field ? 'primary.main' : '#cbd5e1' }}
                                                        />
                                                    </Box>
                                                </TableCell>
                                            ))}
                                            <TableCell
                                                sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } }}
                                                align="center"
                                                onClick={() => toggleSort('timesContacted')}
                                            >
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                                    Times contacted
                                                    <Iconify icon={sortIcon('timesContacted')} width={13} height={13} sx={{ color: sortModel.field === 'timesContacted' ? 'primary.main' : '#cbd5e1' }} />
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } }}
                                                onClick={() => toggleSort('createdAt')}
                                            >
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                                                    {dateColumnLabel}
                                                    <Iconify icon={sortIcon('createdAt')} width={13} height={13} sx={{ color: sortModel.field === 'createdAt' ? 'primary.main' : '#cbd5e1' }} />
                                                    {/* Separate click area for the date-format toggle */}
                                                    <Tooltip title="Change date format" arrow>
                                                        <Box
                                                            component="span"
                                                            sx={{ display: 'inline-flex', ml: 0.3, p: 0.25, borderRadius: '4px', '&:hover': { bgcolor: '#f1f5f9' } }}
                                                            onClick={(e) => { e.stopPropagation(); setDateFormatAnchor(e.currentTarget); }}
                                                        >
                                                            <Iconify
                                                                icon="mdi:calendar-filter-outline"
                                                                width={15}
                                                                height={15}
                                                                sx={{ color: '#64748b' }}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{ fontWeight: 600, fontSize: '12px', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none', '&:hover': { color: 'primary.main' } }}
                                                align="right"
                                                onClick={() => toggleSort('price')}
                                            >
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                                                    Price
                                                    <Iconify icon={sortIcon('price')} width={13} height={13} sx={{ color: sortModel.field === 'price' ? 'primary.main' : '#cbd5e1' }} />
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableHead>
                                <TableBody>
                                    {eventSource ? filteredData.map((row, idx) => (
                                        <TableRow key={row.id ?? idx} hover>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                {row.leadUnavailable ? (
                                                    <Typography sx={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                        {row.leadName}
                                                    </Typography>
                                                ) : (
                                                    <Link
                                                        href={`/dashboard/practice/${practiceId}/lead-detail/${row.leadId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ color: 'primary.main', fontWeight: 500 }}
                                                    >
                                                        {row.leadName}
                                                    </Link>
                                                )}
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: '12px',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 240,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={row.treatment || ''}
                                            >
                                                {row.treatment}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{row.source}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{row.eventType?.replace(/_/g, ' ') || '—'}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatEventTime(row.eventTime)}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row.description || ''}>
                                                {row.description}
                                            </TableCell>
                                        </TableRow>
                                    )) : filteredData.map((row, idx) => (
                                        <TableRow
                                            key={`${row.leadId}-${row.treatment}-${idx}`}
                                            hover
                                        >
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{idx + 1}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Link
                                                        href={`/dashboard/practice/${practiceId}/lead-detail/${row.leadId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        underline="hover"
                                                        sx={{ color: 'primary.main', fontWeight: 500 }}
                                                    >
                                                        {row.leadName}
                                                    </Link>
                                                    {row.isArchived && (
                                                        <Tooltip
                                                            arrow
                                                            title={
                                                                row.archivedAt && parseApiDate(row.archivedAt)?.isValid()
                                                                    ? `Archived ${parseApiDate(row.archivedAt).format('DD/MM/YYYY')}`
                                                                    : 'Archived'
                                                            }
                                                        >
                                                            <Box
                                                                component="span"
                                                                aria-label="Archived"
                                                                sx={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    width: 16,
                                                                    height: 16,
                                                                    borderRadius: '50%',
                                                                    bgcolor: '#dc2626',
                                                                    color: '#fff',
                                                                    fontSize: '10px',
                                                                    fontWeight: 700,
                                                                    lineHeight: 1,
                                                                    flexShrink: 0,
                                                                }}
                                                            >
                                                                A
                                                            </Box>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    fontSize: '12px',
                                                    whiteSpace: 'nowrap',
                                                    maxWidth: 240,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                }}
                                                title={row.treatment || ''}
                                            >
                                                {row.treatment}
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Chip
                                                    label={STATUS_COLORS[row.status]?.label || row.status?.replace(/_/g, ' ')}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '10px',
                                                        height: 20,
                                                        bgcolor: alpha(STATUS_COLORS[row.status]?.text || '#3b82f6', 0.12),
                                                        color: STATUS_COLORS[row.status]?.text || '#3b82f6',
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ whiteSpace: 'nowrap', py: 0.75 }}>
                                                <Typography sx={{ fontSize: '12px', fontWeight: 500, lineHeight: 1.4 }}>{row.source}</Typography>
                                                {row.sourceSecondary && (
                                                    <Typography sx={{ fontSize: '11px', color: '#64748b', lineHeight: 1.3, mt: 0.25 }}>
                                                        {row.sourceSecondary}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }} align="center">{row.timesContacted || 0}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}>{formatDate(row.createdAt)}</TableCell>
                                            <TableCell sx={{ fontSize: '12px', whiteSpace: 'nowrap' }} align="right">
                                                {row.price > 0 ? `£${row.price.toLocaleString()}` : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {/* Only shown when there's no error AND no rows — an error must never
                                      * render like an empty result (see the loadError branch above). */}
                                    {!loadError && filteredData.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={eventSource ? 7 : 8} sx={{ textAlign: 'center', py: 4, color: '#94a3b8' }}>
                                                {eventSource ? 'No events' : 'No data'}
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>
                    )}
                </Box>

                {/* Date format popover */}
                <Popover
                    open={Boolean(dateFormatAnchor)}
                    anchorEl={dateFormatAnchor}
                    onClose={() => setDateFormatAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            sx: {
                                mt: 0.5,
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
                                minWidth: 140,
                            },
                        },
                    }}
                >
                    <List dense sx={{ py: 0.5 }}>
                        {DATE_TITLES.map((option) => (
                            <ListItemButton
                                key={option.value}
                                onClick={() => handleDateFormatChange(option.value)}
                                selected={dateFormat === option.value}
                                sx={{ py: 1, px: 2 }}
                            >
                                <Iconify
                                    icon={option.icon}
                                    width={18}
                                    height={18}
                                    sx={{ mr: 1.5, color: dateFormat === option.value ? 'primary.main' : '#64748b' }}
                                />
                                <ListItemText
                                    primary={option.label}
                                    primaryTypographyProps={{
                                        fontSize: '13px',
                                        fontWeight: dateFormat === option.value ? 600 : 400,
                                    }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                </Popover>
            </Dialog>

            {/* Audience Dialog — popupContext lets the dialog pre-fill filters,
              * echo the user's current view in a banner, and drive the live
              * lead-count preview. */}
            <ReportsAudienceDialog
                generateAudience={generateAudience}
                handleCloseAudienceDialog={handleCloseAudienceDialog}
                currentTab={currentTab}
                onChangeTab={onChangeTab}
                handleCreateAudienceSuccess={handleCreateAudienceSuccess}
                popupContext={{
                    status,
                    statusLabel: statusConfig.label,
                    treatmentName,
                    practiceId,
                    dateRange,
                }}
            />

            {/* Success Confirmation Dialog */}
            <Dialog open={confirmation} onClose={() => setConfirmation(false)}>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Box
                        sx={{
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            bgcolor: alpha('#10b981', 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 'auto',
                            mb: 2,
                        }}
                    >
                        <Iconify icon="akar-icons:circle-check-fill" width={32} sx={{ color: '#10b981' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                        Audience Created!
                    </Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Audience &apos;{audienceName}&apos; created successfully
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => setConfirmation(false)}
                        sx={{ textTransform: 'none', borderRadius: '8px', px: 4 }}
                    >
                        Done
                    </Button>
                </Box>
            </Dialog>
        </>
    );
}

TreatmentLeadPopup.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    treatmentName: PropTypes.string,
    practiceIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    trustServerFilter: PropTypes.bool,
    archivedByCreated: PropTypes.bool,
    revenueMode: PropTypes.bool,
    categoryName: PropTypes.string,
    sourceTypeFilter: PropTypes.string,
    referralFilter: PropTypes.string,
    landingUrlFilter: PropTypes.string,
    assignedToFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    archivedReasonFilter: PropTypes.string,
    status: PropTypes.string,
    viewMode: PropTypes.oneOf(['activity', 'cohort']),
    dateRange: PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
    // Event-sourced drilldown (By Team). When set, the popup fetches from the
    // UserEvent API instead of /leads/ — see the fetchEvents effect above.
    eventSource: PropTypes.shape({
        practiceUserId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        eventType: PropTypes.string,
        startDate: PropTypes.string,
        endDate: PropTypes.string,
    }),
};

TreatmentLeadPopup.defaultProps = {
    practiceId: null,
    treatmentId: null,
    treatmentItemId: null,
    treatmentName: '',
    practiceIds: null,
    trustServerFilter: false,
    archivedByCreated: false,
    revenueMode: false,
    categoryName: '',
    sourceTypeFilter: '',
    referralFilter: '',
    landingUrlFilter: '',
    assignedToFilter: null,
    archivedReasonFilter: '',
    status: 'All',
    viewMode: 'activity',
    dateRange: null,
    eventSource: null,
};
